import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

const refreshAuthToken = async () => {
  const doRefresh = async () => {
    const oldToken = localStorage.getItem('token');
    if (!oldToken) throw new Error('No token to refresh');

    const { data } = await axios.post(
      `${api.defaults.baseURL}/auth/refresh`,
      null,
      { headers: { Authorization: `Bearer ${oldToken}` } }
    );

    const newToken = data?.data?.token || data?.token;
    if (!newToken) throw new Error('Refresh returned no token');

    localStorage.setItem('token', newToken);
    return newToken;
  };

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await doRefresh();
    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const clearAndRedirect = () => {
  const hadToken = !!localStorage.getItem('token');
  localStorage.removeItem('token');
  if (hadToken && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('[API] Request error:', error);
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const { config, response } = error;

    if (process.env.NODE_ENV === 'development') {
      console.error('[API] Response error:', response?.status, config?.url, response?.data);
    }

    if (response?.status === 401 && config && !config._retryAuth) {
      config._retryAuth = true;
      try {
        const newToken = await refreshAuthToken();
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config);
      } catch (refreshError) {
        clearAndRedirect();
      }
    }

    return Promise.reject(error);
  }
);

export default api;